import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Store } from 'lucide-react';
import { useEffect, useState } from 'react';
import logo from '../../../assets/logos/aye-logo.png';
import PayNetFooter from '../../../shared/PayNetFooter';
import type {
  CardBrand,
  EWalletBrand,
  PaymentMethodBrand
} from '../../../types/registration-payment.types';
import { formatCurrency } from '../../../utils/fiat-currency.util';
import ImportantNotesDialog from '../RegisterScreen/components/ImportantNotesDialog';
import { requiresImportantNotes } from '../RegisterScreen/constants/payment-method.const';
import { useFetchGateway } from '../api/hooks';
import { SelectCardBrandStep } from '../RegisterScreen/components/SelectCardBrandStep';
import { SelectEWalletBrandStep } from '../RegisterScreen/components/SelectEWalletBrandStep';
import { SelectPaymentTypeStep } from '../RegisterScreen/components/SelectPaymentTypeStep';
import type { CheckoutInfo } from '../flow/types';
import { StepBanner } from './StepBanner';

interface SelectPaymentMethodProps {
  mode: 'required' | 'optional';
  store: CheckoutInfo | null;
  amount?: number;
  currency?: string;
  cardsOnly: boolean;
  eWalletEnabled: boolean;
  busy: boolean;
  onPick: (brand: PaymentMethodBrand) => void;
  onExit: () => void;
}

type View = 'type' | 'cards' | 'ewallets';

export function SelectPaymentMethod({
  mode,
  store,
  amount,
  currency,
  cardsOnly,
  eWalletEnabled,
  busy,
  onPick,
  onExit
}: Readonly<SelectPaymentMethodProps>) {
  const [view, setView] = useState<View>('type');
  const [pending, setPending] = useState<PaymentMethodBrand | null>(null);
  const [resolving, setResolving] = useState(false);
  const fetchGateway = useFetchGateway();
  const required = mode === 'required';

  // Back from the gateway can restore this page from the browser's
  // back-forward cache with the redirect notice still open.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setPending(null);
        setResolving(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // The notes (about the temporary authorisation charge) only apply to Fiuu. When the gateway
  // cannot be resolved, the brand's static config decides.
  const confirmBrand = async (brand: CardBrand | EWalletBrand) => {
    if (resolving) return;
    setResolving(true);
    let gateway: string | undefined;
    try {
      gateway = (await fetchGateway(brand)).gateway;
    } catch {
      gateway = undefined;
    }
    setResolving(false);
    if (requiresImportantNotes(brand, gateway)) setPending(brand);
    else onPick(brand);
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-white">
      <img src={logo} alt="Aye Logo" className="mt-9 h-8 w-16 flex-shrink-0" />

      <div className="flex w-full flex-1 flex-col items-center">
        {view === 'cards' && (
          <SelectCardBrandStep
            initialBrand={null}
            onConfirm={confirmBrand}
            onBack={() => setView('type')}
            isLoading={busy || resolving}
          />
        )}
        {view === 'ewallets' && (
          <SelectEWalletBrandStep
            initialBrand={null}
            onConfirm={confirmBrand}
            onBack={() => setView('type')}
            isLoading={busy || resolving}
          />
        )}

        {view === 'type' && (
          <>
            <div className="mt-6 w-full max-w-md px-6 sm:px-0">
              {required ? (
                <StepBanner
                  tone="final"
                  label="FINAL STEP"
                  text="Account created. Add a payment method to pay."
                />
              ) : (
                <StepBanner
                  tone="optional"
                  label="OPTIONAL STEP"
                  text="Account created. Add a payment method, or skip for now."
                />
              )}

              {store && (
                <div className="mt-4 flex items-center gap-3 rounded-2xl border-2 border-gray-200 p-3 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-500">
                    <Store className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                      {required ? 'PAYING' : 'AT'}
                    </p>
                    <p className="truncate text-sm font-bold text-gray-900">
                      {store.storeName}
                    </p>
                  </div>
                  {required && amount !== undefined && (
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(amount, currency)}
                    </p>
                  )}
                </div>
              )}
            </div>

            <fieldset
              disabled={busy}
              className="m-0 flex w-full min-w-0 flex-col items-center border-0 p-0"
            >
              <SelectPaymentTypeStep
                onSelectCards={() => setView('cards')}
                onSelectEWallets={() => setView('ewallets')}
                onSelectDuitNow={() => undefined}
                onSkipPayment={onExit}
                onBack={onExit}
                backLabel={null}
                isEWalletEnabled={eWalletEnabled}
                isDuitNowEnabled={false}
                cardsOnly={cardsOnly}
                duitNowCheckoutNote={
                  required
                    ? "Bank approval can take a few minutes, so it can't be used to pay this bill. You can link it later from Payment Methods Overview in your account."
                    : "Bank approval can take a few minutes, so it can't be used for this checkout. You can link it later from Payment Methods Overview in your account."
                }
              />

              <div className="mt-6 flex w-full max-w-md flex-col items-center px-6 sm:px-0">
                <p className="flex items-center gap-1 text-xs text-gray-500">
                  <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
                  Stores never see your card or account details
                </p>
                <button
                  type="button"
                  onClick={onExit}
                  className="mt-4 rounded-2xl border-2 border-gray-200 px-8 py-3 text-sm font-semibold text-gray-900 transition-all hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {required ? 'Pay another way' : 'Skip, back to checkout'}
                </button>
              </div>
            </fieldset>
          </>
        )}
      </div>

      <div className="my-4 flex justify-center">
        <PayNetFooter />
      </div>

      <ImportantNotesDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onComplete={() => pending && onPick(pending)}
      />
    </div>
  );
}
